// Install necessary packages before running:
// npm install @mui/material @emotion/react @emotion/styled formik

import React, { useState } from "react";
import { Button, TextField, Grid2, Divider, Box, Typography, Stack, Alert, Avatar, AlertTitle } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Error } from "@mui/icons-material";
import { CheckCircleOutline, Login, CelebrationOutlined, EmailOutlined } from '@mui/icons-material';
import { AWS_LINK, DEV_LINK } from "../Static/static";


// Validation Schema for Formik
const RegisterSchema = Yup.object().shape({
  fullname: Yup.string().required("Fullname is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  password_confirm: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  organization_name: Yup.string().required("Organization name is required"),
  address: Yup.string().required("Address"),
  zip_code: Yup.string().required("Zip code is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required."),
  code: Yup.string().required("Register code required.")
});

const initialValues = {
    email: null,
    password: null,
    organization_name: null,
    address: null,
    zip_code: null,
    state: null,
    city: null,
    code: null,
    fullname: null
}

export default function RegisterPage(){
    const [error, setError] = useState(null);
    const [completed, setCompleted] = useState(false);
    const handleSubmit = async (values) => {
      if (values.password != values.password_confirm) {
          console.log("Passwords do not match try again.");
          setError("Passwords do not match.");
          return;
      }
      setError(null);
      let payload = {...values}
      let json_data = JSON.stringify(payload);
      try {
          const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;
          let response = await axios.post(LINK + "/create_organization", json_data);
          if (response.data.status == "OK") {
              setCompleted(true);
              return;
          } else {
              setCompleted(false);
              setError('Unable to create organization');
              return;
          }
      }
      catch (error) {
          setCompleted(false);
          setError('Unable to create organization');
          return;
      }
    };

    return (
      <Box
      sx={{
        width: "100%",
        maxWidth: 450,
        margin: "auto",
        mt: 8,
        p: 4,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 1,
        bgcolor: 'background.paper',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: 'primary.main',
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3
        }
      }}
    >
      <Box textAlign="center" mb={3}>
        <Typography 
          fontWeight={700} 
          variant="h4" 
          sx={{ 
            color: 'primary.main',
            letterSpacing: '0.5px',
            mb: 1
          }}
        >
          Create Root Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Register a root user to manage administrative processes and tutors
        </Typography>
      </Box>
    
      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert 
            icon={<Error />} 
            severity="error"
            sx={{
              borderRadius: 1,
              boxShadow: 'none'
            }}
          >
            {error}
          </Alert>
        </Box>
      )}
    
      {completed ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '45vh',
          p: 2,
        }}>
          <Box sx={{
            position: 'relative',
            maxWidth: 500,
            width: '100%',
            p: 4,
            pt: 8,
            borderRadius: 4,
            textAlign: 'center',
            boxShadow: 3,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '40%',
              opacity: 0.05,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              zIndex: 0
            }
          }}>
            {/* Checkmark Avatar */}
            <Avatar sx={{
              position: 'absolute',
              top: -40,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 80,
              bgcolor: 'success.light',
              boxShadow: 3,
              zIndex: 1
            }}>
              <CheckCircleOutline sx={{ fontSize: 48 }} color="success" />
            </Avatar>
    
            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h4" component="h1" sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(45deg, primary.main, primary.light)',
                WebkitBackgroundClip: 'text',
              }}>
                Welcome Aboard!
              </Typography>
    
              <Typography variant="body1" color="text.secondary" paragraph>
                Your account has been successfully created. Please log in with the credentials used.
              </Typography>
    
              <Box sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                my: 3,
                px: 3,
                py: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 2
              }}>
                <EmailOutlined color="action" />
                <Typography variant="body2">
                  Please verify your email to get started
                </Typography>
              </Box>
    
              <Divider sx={{ my: 3, width: '80%', mx: 'auto' }} />
    
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 3
              }}>
                <CelebrationOutlined color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Ready to explore?
                </Typography>
              </Box>
    
              <Button
                variant="contained"
                size="large"
                startIcon={<Login />}
                href="/login"
                sx={{
                  px: 4,
                  fontWeight: 'bold',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme => `0 4px 12px ${theme.palette.primary.light + '30'}`
                  }
                }}
              >
                Go to Login
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={RegisterSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <Stack spacing={2} mb={3}>
                <Field
                  as={TextField}
                  name="fullname"
                  label="Full Name"
                  fullWidth
                  variant="outlined"
                  error={touched.fullname && Boolean(errors.fullname)}
                  helperText={touched.fullname && errors.fullname}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Field
                  as={TextField}
                  name="email"
                  label="Root Email"
                  fullWidth
                  variant="outlined"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Field
                  as={TextField}
                  name="password"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Field
                  as={TextField}
                  name="password_confirm"
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  error={touched.password_confirm && Boolean(errors.password_confirm)}
                  helperText={touched.password_confirm && errors.password_confirm}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Field
                  as={TextField}
                  name="organization_name"
                  label="Organization Name"
                  fullWidth
                  variant="outlined"
                  error={touched.organization_name && Boolean(errors.organization_name)}
                  helperText={touched.organization_name && errors.organization_name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Field
                  as={TextField}
                  name="address"
                  label="Address"
                  fullWidth
                  variant="outlined"
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
    
                <Grid2 container spacing={2}>
                  <Grid2 item size={{xs: 6}}>
                    <Field
                      as={TextField}
                      name="city"
                      label="City"
                      fullWidth
                      variant="outlined"
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 item size={{xs: 6}}>
                    <Field
                      as={TextField}
                      name="state"
                      label="State"
                      fullWidth
                      variant="outlined"
                      error={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1
                        }
                      }}
                    />
                  </Grid2>
                </Grid2>
    
                <Field
                  as={TextField}
                  name="zip_code"
                  label="Zip Code"
                  fullWidth
                  variant="outlined"
                  error={touched.zip_code && Boolean(errors.zip_code)}
                  helperText={touched.zip_code && errors.zip_code}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
                <Alert variant="standard" color="warning">
                  <AlertTitle>
                    Please reach out for a registration code.
                  </AlertTitle>
                  <Typography variant="caption">Email: Lag.webservices@gmail.com</Typography>
                </Alert>
                <Field
                  as={TextField}
                  name="code"
                  label="Registration Code"
                  variant="outlined"
                  error={touched.code && Boolean(errors.code)}
                  helperText={touched.code && errors.code}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
              </Stack>
    
              <Button 
                type="submit" 
                variant="contained" 
                fullWidth 
                sx={{ 
                  py: 1.5,
                  borderRadius: 1,
                  fontWeight: 'bold',
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Register
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </Box>
    );
};

